/* Created by Interactive 63 - https://interactive63.com */
/**
 * The compiler: turns a project into scoped SVG markup and CSS keyframes. It is pure, so the
 * editor preview and every export are produced by exactly the same code. Exports add accessible
 * attributes, ID prefixing, the CSS-only hover trigger, reduced motion rules and minification.
 */
import { collectAnimated, isExcluded, resolveTiming } from './animated';
import { buildCss } from './css';
import { collectWords, wordsCss, wordsReducedMotionRules } from './words';
import { clickCss, hoverPauseCss, pausedCss, reducedMotionCss, responsiveCss } from './export-css';
import { buildMorphs, morphWarnings } from '../morph/morph-track';
import { buildMarkup } from './markup';
import { morphAnimationEntries, morphCss, morphReducedMotionRules } from './morph-css';
import { CREDIT_CSS, minifyCss, minifyMarkup } from './minify';
import { buildRuntimeConfig, type RuntimeConfig } from './runtime-config';
import { triggerNeedsRuntime } from './triggers';
import type { Project } from '../model/types';
import { bakeMotion } from './motion-bake';
import type { CompileOptions, CompileWarning, CompiledScene } from './types';

/** Compiles a project for the preview or for export. */
export function compileScene(source: Project, options: CompileOptions): CompiledScene {
  const { project, warnings: motionWarnings } = bakeMotion(source);
  const exporting = options.target === 'export';
  const settings = options.exportSettings;
  const sceneName = settings.sceneName || 'scene';
  const sceneClass = `st-${sceneName}`;
  const baseTiming = resolveTiming(project, options.target);
  const timing =
    exporting && options.iterations !== undefined
      ? { ...baseTiming, iterations: options.iterations }
      : baseTiming;
  const animated = collectAnimated(project, options.target);
  const wordBuilds = collectWords(project, options.target);

  const morphBuilds = buildMorphs(
    project,
    (id) => options.target === 'preview' || !isExcluded(project, id),
  );
  const markup = buildMarkup(
    project,
    animated,
    options.target,
    exporting ? settings : undefined,
    morphBuilds,
    wordBuilds,
  );
  // A CSS-only export animates morphs from the same rule as the element's other tracks.
  const cssMorphs = exporting && settings.codeType === 'css';
  const built = buildCss(
    animated,
    timing,
    { sceneName, sceneClass },
    cssMorphs ? morphAnimationEntries(morphBuilds, timing, sceneName) : undefined,
  );
  const words = wordsCss(wordBuilds, timing, { sceneName, sceneClass });
  // Words animate separate spans, so their rules simply follow the element rules.
  const animationCss = built.css + words.css;
  const animatedSelectors = [...built.animatedSelectors, ...words.selectors];
  const morphs = morphBuilds.flatMap((b) => (b.result.ok ? [b.result.data] : []));
  const warnings: CompileWarning[] = [
    ...markup.warnings,
    ...built.warnings,
    ...morphWarnings(morphBuilds, project),
    ...motionWarnings,
  ];

  if (exporting && animated.length === 0 && morphs.length === 0 && words.selectors.length === 0) {
    warnings.push({
      code: 'nothing-animated',
      message: 'Nothing is animated yet. Add keyframes in the timeline, then export again.',
    });
  }

  const needsRuntime = project.tracks.some(
    (t) =>
      t.enabled &&
      t.property === 'morph' &&
      t.keyframes.length > 0 &&
      (options.target === 'preview' || !isExcluded(project, t.elementId)),
  );

  let css = animationCss;
  let svgMarkup = markup.markup;
  let runtimeConfig: RuntimeConfig | null = null;
  const wrapperAttributes: Record<string, string> = {};
  if (exporting) {
    const parts: string[] = [];
    const { trigger, reducedMotion } = project.scene;
    let effective = trigger;
    if (settings.codeType === 'css' && triggerNeedsRuntime(trigger)) {
      effective = { type: 'auto' };
      warnings.push({
        code: 'trigger-needs-runtime',
        message:
          'The chosen start trigger needs the JS runtime, so this CSS-only export starts automatically.',
      });
    }
    const withRuntime = settings.codeType === 'css-js';
    if (settings.responsive) parts.push(responsiveCss(sceneClass));
    if (animationCss !== '') parts.push(animationCss.trimEnd());
    if (!withRuntime && morphs.length > 0) {
      parts.push(morphCss(morphBuilds, timing, sceneName, sceneClass, built.mergedMorphs));
      warnings.push({
        code: 'morph-css-safari',
        message:
          "Shape morphing in CSS-only exports doesn't work in Safari. Choose CSS + JS for full browser support.",
      });
    }
    if (withRuntime) {
      runtimeConfig = buildRuntimeConfig(project, settings, options.iterations, morphs);
      wrapperAttributes['data-svg-tweener'] = '';
      if (effective.type !== 'auto') {
        // The artwork rests on its start pose until the script fires the trigger.
        const paused = pausedCss(animatedSelectors);
        if (paused !== '') parts.push(paused);
      }
      if (effective.type === 'click') {
        parts.push(clickCss(sceneClass));
        wrapperAttributes['tabindex'] = '0';
        wrapperAttributes['role'] = 'button';
        wrapperAttributes['aria-label'] = settings.accessibleTitle.trim() || 'Play animation';
      }
    } else if (effective.type === 'hover') {
      const hover = hoverPauseCss(animatedSelectors, sceneClass);
      if (hover !== '') parts.push(hover);
    }
    const reduced = reducedMotionCss(
      project,
      animated,
      { sceneName, sceneClass },
      reducedMotion,
      timing.duration,
      options.forceReducedMotion === true,
      [
        ...(!withRuntime && reducedMotion !== 'ignore'
          ? morphReducedMotionRules(
              morphBuilds,
              sceneClass,
              reducedMotion === 'final-frame' ? 'final' : 'initial',
            )
          : []),
        ...(reducedMotion !== 'ignore'
          ? wordsReducedMotionRules(
              wordBuilds,
              { sceneName, sceneClass },
              reducedMotion === 'final-frame' ? 'final' : 'initial',
              timing.duration,
            )
          : []),
      ],
    );
    if (reduced !== '') parts.push(reduced);
    css = parts.length > 0 ? `${parts.join('\n\n')}\n` : '';
    css = `${CREDIT_CSS}\n${css}`;
    if (settings.minify) {
      css = minifyCss(css);
      svgMarkup = minifyMarkup(svgMarkup);
    }
  }

  return {
    sceneClass,
    svgMarkup,
    css,
    runtimeConfig,
    morphs,
    wrapperAttributes,
    needsRuntime,
    warnings,
  };
}
